import { Box, ListItem, Stack } from "@mui/material";
import VirtualizedList, { VirtualizedListItem } from "../virtualized-list/VirtualizedList";

import { useFieldArray, useFormContext } from "react-hook-form";
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import { useTranslation } from "react-i18next";
import { getObjectTypeName } from "../../common/utilities/GetObjectTypeName";
import NoData from "../no-data/NoData";

import FormCheckbox from "../forms/form-checkbox/FormCheckbox";
import { IReferenceNotificationListDTO } from "../../services/setpoint-administration-service/Models";
import NotificationListEditorDialog, { bindNotificationListEditorDialog, useNotificationListEditorDialogState } from "../notification-list-editor/NotificationListEditorDialog";
import { BaseActions } from "../action-menu/ActionMenuItem";
import { useUserContext } from "../../contexts/user-context/UserContext";
import { useQueryNotifications } from "../../queries/UseQueryNotifications";
import { useGroup } from "../../queries/groups/UseGroup";
import { Ref, forwardRef, useImperativeHandle } from "react";
import ActionMenu from "../action-menu/ActionMenu";
import FormNotificationListsUtils, { FormNotificationListsActions, NotificationListsFormType } from "./FormNotificationListsUtils";
import { contains } from "../../common/utilities/Contains";
import { RecipientTypeEnum } from "../../common/enums/RecipientTypeEnum";


export type FormNotificationListsType = {
    executeAction: (action: FormNotificationListsActions | BaseActions) => void;
};


export type FormNotificationListsItem = IReferenceNotificationListDTO & VirtualizedListItem;

export type FormNotificationListsProps = {
    visible?: boolean;
    viewSelected: boolean;
    filterText: string;
    groupID: number;
    organizationID: number | undefined;
    onNotificationListSelected?: (value: boolean) => void;
    allowEdit: boolean;

    recipientType?: RecipientTypeEnum
};

function FormNotificationLists({
    visible,
    viewSelected,
    filterText,
    groupID,
    organizationID,
    onNotificationListSelected,
    allowEdit,
    recipientType
}: FormNotificationListsProps, ref: Ref<unknown> | undefined) {

    const {t} = useTranslation();

    const [userState] = useUserContext();
    const {data: group } = useQueryNotifications(useGroup)({
        customerID: userState.customer?.id as number,
        groupID: groupID
    });


    const {
        getValues,
        watch,
        //trigger,
        control,
        //reset,
        //setValue,
        //resetField
    } = useFormContext<NotificationListsFormType>();    

    const [
        notificationLists,
    ] = watch([
        'notificationLists',
    ]);

    const {
        //fields: notificationListFields,
        append,
        //remove:
        update
    } = useFieldArray({
        control: control,
        name: 'notificationLists',
        keyName: "fieldKey"
    });    


    const notificationListEditorDialog = useNotificationListEditorDialogState();

    const count = FormNotificationListsUtils.filterNotificationLists(notificationLists, filterText, organizationID).length;


    useImperativeHandle<unknown, FormNotificationListsType>(
        ref,
        () => ({
            executeAction: executeAction
        })
    );

    function executeAction(action: FormNotificationListsActions | BaseActions) {

        switch(action) {
            case FormNotificationListsActions.Add:
                onAddNotificationList();
                break;
        }
    }


    async function onAddNotificationList() {

        const orgID = organizationID ?? group?.organizationID as number;

        const result = await notificationListEditorDialog.open({
            groupID: groupID,
            organizationID: orgID,
            notificationListID: -1,
            recipientType: recipientType
        });

        if (result.reason === "save") {

            const values = result.values?.notificationList;
            if (values) {
                const newNotificationList: IReferenceNotificationListDTO = {
                    id: values.id,
                    name: values.name,
                    organizationID: orgID,
                    selected: false
                };

                append(newNotificationList);
            }
        }
    }

    async function onEditNotificationList(item: IReferenceNotificationListDTO) {

        const result = await notificationListEditorDialog.open({
            groupID: groupID,
            organizationID: item.organizationID,
            notificationListID: item.id
        });

        if (result.reason === "save") {


            const values = result.values?.notificationList;
            if (values) {
                const newNotificationList: IReferenceNotificationListDTO = {
                    id: values.id,
                    name: values.name,
                    organizationID: item.organizationID
                };


                const ndx = notificationLists.findIndex(o => o.id === item.id);

                update(ndx, newNotificationList);
            }
        }
    }

    function onNotificationListChanged(value: boolean) {

        onNotificationListSelected && onNotificationListSelected(value);
    }

    function renderItem(_index: number, item: IReferenceNotificationListDTO) {        

        const itemIndex = notificationLists.findIndex(v => v.id === item.id);
        
        return (
            <ListItem key={item.id} 
                dense
                secondaryAction={

                    allowEdit ?
                    <ActionMenu
                        actionItems={[
                            {
                                label: t("buttons.editNotificationList"),
                                //icon: () => <FontAwesomeIcon icon={faSquarePlus} size="1x" />,
                                action: FormNotificationListsActions.Add,
                                onClick: () => {onEditNotificationList(item)}, 
                                order: 0
                            }
                        ]}
                    /> : 
                    undefined
                  }
                >
                <FormCheckbox
                    name={`notificationLists.${itemIndex}.selected`}
                    label={item.name}
                    sx={{ 
                        marginRight: 0,
                        marginTop: 0,
                        //textOverflow: "ellipsis",
                        //whiteSpace: "nowrap",
                        //overflow: "hidden",
                        // '& > *': {
                        //     fontSize: '.9rem'
                        // }
                    }}
                    typographyProps={{
                        sx: {
                            fontSize: '.95rem',
                        }
                    }}
                    onChange={(_event, checked) => onNotificationListChanged(checked)}
                    />

            </ListItem>                    
        );
    }

    function onFilterItem(item: FormNotificationListsItem): boolean {

        const itemIndex = notificationLists.findIndex(v => v.id === item.id);
        
        const value = getValues(`notificationLists.${itemIndex}`);        

        let show = !!contains(value.name, filterText);
        if (show && organizationID) {
            show = value.organizationID === organizationID;
        }

        if (show && viewSelected) {
            show = value.selected ?? false;
        }

        return show;
    }

    return (visible ? <Stack
        direction='row'
        sx={{
            display: 'flex',
            flex: '1 1 auto'
        }}>
        <Box
            sx={{
                display: 'flex',
                flex: '1 1 auto',
                flexDirection: 'column',

                //paddingTop: 1,
                overflowY: 'auto'
            }}>
            <Box sx={{
                display: "flex",
                flexDirection: "column",
                flexGrow: 1,
                overflow: "hidden"
                }}>

                {count === 0 &&         
                    <Box sx={{
                        padding: 1
                    }}>
                        <NoData text={t('setpointAdmin.nodata.noFilterMatch', {name: getObjectTypeName(eLynxObjectTypeEnum.NotificationList, true, true)})} />
                    </Box>
                }
                
                <VirtualizedList
                    items={notificationLists}
                    renderItem={renderItem}
                    filter={onFilterItem}
                />
            </Box>
        </Box>


        <NotificationListEditorDialog {...bindNotificationListEditorDialog(notificationListEditorDialog)} />

    </Stack> : null);
}

export default forwardRef(FormNotificationLists);